import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CardContent, CardHeader, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Stack, TextField, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import CenteredCard from "../../components/Card/CenteredCardLayout";
import DefaultActions from "../../components/Card/DefaultActions";
import LocalizedNumberInputs from "../../components/Input/LocalizedNumberInputs";
import { useAuth, useLoading, useNavigation, useSnackbar } from "../../Context/Contexts";
import { ProgressResultViewModel } from "../../Types/Progress/ProgressResultViewModel";
import { SalesPriceImportLine, SalesPriceImportResult } from "../../Types/Sales/SalesPriceImportTypes";

const SalesPriceImportPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { importId = "" } = params || {};
  const { t } = useTranslation();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { showLoading, hideLoading, isLoading } = useLoading();
  const { navigate } = useNavigation();
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [lines, setLines] = useState<SalesPriceImportLine[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [statusText, setStatusText] = useState<string>("");
  const [editingLine, setEditingLine] = useState<SalesPriceImportLine | null>(null);

  useEffect(() => {
    if (isRunning) showLoading();
    else hideLoading();
  }, [isRunning, showLoading, hideLoading]);

  useEffect(() => {
    fetchLines();
  }, [importId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setPendingFile(file ?? null);
  };

  const fetchLines = async (id?: string) => {
    const activeId = id ?? importId;
    if (!activeId) return;
    try {
      const response = await authService.post<SalesPriceImportLine[]>(`/salesPriceApi/GetImportLines?importId=${activeId}`, {});
      setLines(response ?? []);
    } catch (error: any) {
      showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
    }
  };

  const pollStatus = async (progressId?: number | null, onDone?: () => Promise<void> | void) => {
    if (!progressId) return;
    let attempts = 120;

    while (attempts > 0) {
      try {
        const response = await authService.get<ProgressResultViewModel>(`/progressApi/GetProgress?progressId=${progressId}`);
        if (!response) break;

        setStatusText(`${response.text ?? ""} (${response.progress ?? 0}%)`);

        if (response.error) {
          showSnackbar(response.text ?? t("dialog.genericError"), "error");
          if (onDone) await onDone();
          break;
        }

        if (response.done) {
          if (onDone) await onDone();
          break;
        }
      } catch (error: any) {
        showSnackbar(`${t("dialog.genericError")}: ${error.response?.data ?? error.message}`, "error");
        fetchLines();
        break;
      }

      attempts--;
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  };

  const handleImport = async () => {
    if (!pendingFile) {
      showSnackbar(t("help.invalidFile"), "warning");
      return;
    }

    setIsRunning(true);
    setStatusText("");
    const formData = new FormData();
    formData.append("file", pendingFile);
    formData.append("importId", importId);
    try {
      const response = await authService.post<SalesPriceImportResult>('/salesPriceApi/CreateSalesPriceImport', formData);
      await pollStatus(response.progressId, async () => {
        await fetchLines(response.importId);
        showSnackbar(t("dialog.savedSuccessfully"), "success");
      });
    } catch (error: any) {
      showSnackbar(`${t("dialog.fileUploadError")}: ${error.response?.data ?? error.message}`, "error");
    } finally {
      setIsRunning(false);
    }
  };

  const handleSaveLine = async () => {
    if (!editingLine || !importId) return;
    if (editingLine.completed) {
      showSnackbar(t("dialog.cannotChangeCompletedLine"), "warning");
      return;
    }
    setIsRunning(true);
    try {
      const updatedLine = await authService.post<SalesPriceImportLine>(`/salesPriceApi/CreateOrUpdateImportLine?importId=${importId}`, editingLine);
      setLines((prev) => prev.map((line) => (line.id === updatedLine.id ? updatedLine : line)));
      setEditingLine(null);
      showSnackbar(t("dialog.lineSaved"), "success");
    } catch (error: any) {
      showSnackbar(`${t("dialog.lineSaveError")} : ${error.response?.data ?? error.message}`, "error");
    } finally {
      setIsRunning(false);
    }
  };

  const handleDeleteLine = async (line?: SalesPriceImportLine | null) => {
    if (!line || !importId || !line.id) return;
    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    if (line.completed) {
      showSnackbar(t("dialog.cannotChangeCompletedLine"), "warning");
      return;
    }
    setIsRunning(true);
    try {
      await authService.post(`/salesPriceApi/DeleteImportLine?importId=${importId}`, line);
      setLines((prev) => prev.filter((x) => x.id !== line.id));
      setEditingLine(null);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");
    } finally {
      setIsRunning(false);
    }
  };

  const handleDeleteImport = async () => {
    if (!importId) {
      navigate({ path: "/sales/pricelist/list" });
      return;
    }

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;

    setIsRunning(true);
    try {
      await authService.post(`/salesPriceApi/DeleteImport?importId=${importId}`, {});
      showSnackbar(t("dialog.deletedSuccessfully"), "success");
      navigate({ path: "/sales/pricelist/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");
    } finally {
      setIsRunning(false);
    }
  };

  const handleImportFromPreview = async () => {
    if (!importId) return;
    setIsRunning(true);
    setStatusText("");
    try {
      const response = await authService.post<SalesPriceImportResult>(`/salesPriceApi/ImportFromPreview?importId=${importId}`, {});
      await pollStatus(response.progressId, async () => {
        await fetchLines(importId);
        showSnackbar(t("dialog.savedSuccessfully"), "success");
      });
    } catch (error: any) {
      showSnackbar(`${t("dialog.genericError")}: ${error.response?.data ?? error.message}`, "error");
    } finally {
      setIsRunning(false);
    }
  };

  const filteredLines = useMemo(() => {
    if (!showErrorsOnly) return lines;
    return lines.filter((line) => line.containsErrors);
  }, [lines, showErrorsOnly]);

  const hasCompletedImport = useMemo(() => lines.some((line) => line.completed), [lines]);

  const getError = (field: string) =>
    editingLine?.errors?.find((error) => error.field?.toLowerCase() === field.toLowerCase())?.error;

  const hasError = (field: string) =>
    editingLine?.errors?.some((error) => error.field?.toLowerCase() === field.toLowerCase());

  const handleFieldChange = <K extends keyof SalesPriceImportLine>(field: K, value: SalesPriceImportLine[K]) => {
    setEditingLine((prev) => (prev ? ({ ...prev, [field]: value } as SalesPriceImportLine) : prev));
  };

  const thousandSeparator = useMemo(
    () => Intl.NumberFormat(navigator.language).formatToParts(1000).find((part) => part.type === "group")?.value || ",",
    []
  );

  const decimalSeparator = useMemo(
    () => Intl.NumberFormat(navigator.language).formatToParts(1.1).find((part) => part.type === "decimal")?.value || ".",
    []
  );


  const handleNavigateSalesPriceImport = () => {
    navigate({ path: "/sales/pricelist/list" });
  };

  return (
    <CenteredCard isLoading={isLoading} title={t("common.salesPriceImport")}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          maxWidth: "fit-content",
          mb: 2,
        }}
      >
        <DefaultActions
          onImport={handleImportFromPreview}
          onDelete={handleDeleteImport}
          customActions={[
            { label: t("common.salesPriceList"), onClick: handleNavigateSalesPriceImport },
          ]}
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardHeader title={t("common.import")} subheader={t("help.salesPriceImportDescription")} />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    label={t("common.fileName")}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={pendingFile?.name ?? t("common.noFileSelected")}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    component="label"
                    variant="contained"
                    color="info"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                  >
                    {t("common.uploadFile")}
                    <input type="file" accept=".csv,text/csv" hidden onChange={handleFileChange} />
                  </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleImport}
                    startIcon={<PlayArrowIcon />}
                    fullWidth
                    disabled={isRunning}
                  >
                    {t("common.importFile")}
                  </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleImportFromPreview}
                    startIcon={<PlayArrowIcon />}
                    fullWidth
                    disabled={!importId || isRunning}
                  >
                    {t("common.importData")}
                  </Button>
                </Grid>
                {statusText && (
                  <Grid item xs={12}>
                    <Typography variant="body2" align="center" color="text.secondary">
                      {statusText}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                gap: 2,
              }}
            >

              <Button
                variant="contained"
                fullWidth
                color={showErrorsOnly ? "info" : "error"}
                onClick={() => setShowErrorsOnly((prev) => !prev)}
              >
                {showErrorsOnly ? t("common.showAll") : t("common.showOnlyErrors")}
                  </Button>
     
            </Box>

            {filteredLines.length === 0 ? (
              <Typography align="center" color="text.secondary">
                {t("help.noImportLines")}
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {filteredLines.map((line) => (
                  <Grid item xs={12} key={line.id ?? `${line.no}-${line.variantId}`}>
                    <Box
                      onClick={() => setEditingLine(line)}
                      sx={{
                        p: 1.5,
                        border: "1px solid",
                        borderColor: line.containsErrors ? "error.light" : "divider",
                        borderRadius: 1,
                        position: "relative",
                        backgroundColor: line.containsErrors
                          ? "rgba(255, 0, 0, 0.05)"
                          : line.completed
                            ? "rgba(0, 255, 0, 0.05)"
                            : "background.paper",
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: line.containsErrors
                            ? "rgba(255, 0, 0, 0.08)"
                            : line.completed
                              ? "rgba(0, 255, 0, 0.08)"
                              : "action.hover",
                        },
                      }}
                    >
                      {line.containsErrors && (
                        <Box display="flex" alignItems="center" color="error.main" mb={1}>
                          <WarningAmberIcon sx={{ mr: 1 }} />
                          <Typography variant="body2">{t("help.importRowHasErrors")}</Typography>
                        </Box>
                      )}
                      {line.completed && (
                        <CheckCircleIcon
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: "success.main",
                          }}
                        />
                      )}
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {line.no}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {line.description}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          <Typography variant="body2">{t("common.salesPrice")}:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {line.unitPrice}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          <Typography variant="body2">{t("common.currency")}:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {line.currency}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          <Typography variant="body2">{t("common.originalPrice")}:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {line.originalUnitPrice}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          <Typography variant="body2">{t("common.startDate")}:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {line.startDate}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          <Typography variant="body2">{t("common.endDate")}:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {line.endDate}
                          </Typography>
                        </Grid>
                        {line.errors?.length ? (
                          <Grid item xs={12}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={0.5}>
                              {line.errors.map((err, index) => (
                                <Chip
                                  key={`${err.field}-${index}`}
                                  color="error"
                                  size="small"
                                  label={`${err.field}: ${err.error}`}
                                />
                              ))}
                            </Stack>
                          </Grid>
                        ) : (
                          <Grid item xs={12}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {line.completed ? (
                                <Typography variant="body2" color="success.main">
                                  {t("help.importCompleteMessage")}
                                </Typography>
                              ) : (
                                <>
                                  <HourglassBottomIcon fontSize="small" />
                                  <Typography variant="body2" color="text.secondary">
                                    {t("common.pending")}
                                  </Typography>
                                </>
                              )}
                            </Stack>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      {hasCompletedImport && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("help.importCompleteMessage")}
          </Typography>
        </Box>
      )}
      <Dialog open={!!editingLine} onClose={() => setEditingLine(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: "center" }}>{t("common.edit")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Id"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editingLine?.salesPriceId}
                type="number"
                disabled={ true}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("common.itemNo")}
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editingLine?.no ?? ""}
                onChange={(e) => {
                  handleFieldChange("no", e.target.value);
                }}
                error={hasError("no")}
                helperText={getError("no")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("common.description")}
                fullWidth
                disabled={true}
                InputLabelProps={{ shrink: true }}
                value={editingLine?.description ?? ""}
                onChange={(e) => setEditingLine((prev) => (prev ? { ...prev, description: e.target.value } : prev))}
                error={hasError("description")}
                helperText={getError("description")}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <LocalizedNumberInputs
                label={t("common.salesPrice")}
                value={editingLine?.unitPrice ?? null}
                onValueChange={(value) => {
                  handleFieldChange("unitPrice", value ?? null);
                }}
                allowNegative={false}
                decimalScale={4}
                thousandSeparator={thousandSeparator}
                decimalSeparator={decimalSeparator}
                error={hasError("unitPrice")}
                helperText={getError("unitPrice")}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                label={t("common.currency")}
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editingLine?.currency ?? ""}
                onChange={(e) => {
                  handleFieldChange("currency", e.target.value);
                }}
                error={hasError("currency")}
                helperText={getError("currency")}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <LocalizedNumberInputs
                label={t("common.originalPrice")}
                value={editingLine?.originalUnitPrice ?? null}
                onValueChange={(value) => {
                  handleFieldChange("originalUnitPrice", value ?? null);
                }}
                allowNegative={false}
                decimalScale={4}
                thousandSeparator={thousandSeparator}
                decimalSeparator={decimalSeparator}
                error={hasError("originalunitprice")}
                helperText={getError("originalunitprice")}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                label={t("common.startDate")}
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editingLine?.startDate ?? ""}
                onChange={(e) => {
                  handleFieldChange("startDate", e.target.value);
                }}
                error={hasError("startdate")}
                helperText={getError("startdate")}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                label={t("common.endDate")}
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editingLine?.endDate ?? ""}
                onChange={(e) => {
                  handleFieldChange("endDate", e.target.value);
                }}
                error={hasError("enddate")}
                helperText={getError("enddate")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingLine(null)}>{t("common.cancel")}</Button>
          <Button color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteLine(editingLine)}>
            {t("common.delete")}
          </Button>
          <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={handleSaveLine}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </CenteredCard>
  );
};

export default SalesPriceImportPage;
